import React from 'react';
import styled from 'styled-components';

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 24px;
  flex-wrap: wrap;
  padding: 20px;
  background: var(--background-secondary);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-primary);
  box-shadow: var(--shadow-sm);
`;

const PaginationButton = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  border: 1px solid ${props => props.active ? 'var(--accent-primary)' : 'var(--border-secondary)'};
  background: ${props => props.active 
    ? 'linear-gradient(135deg, var(--accent-primary) 0%, var(--primary-700) 100%)' 
    : 'var(--background-secondary)'};
  color: ${props => props.active ? '#ffffff' : 'var(--text-primary)'};
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  min-width: 40px;
  min-height: 40px;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover:not(:disabled) {
    background: ${props => props.active 
      ? 'linear-gradient(135deg, var(--accent-hover) 0%, var(--primary-800) 100%)' 
      : 'var(--primary-50)'};
    border-color: ${props => props.active ? 'var(--accent-hover)' : 'var(--accent-primary)'};
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
    min-width: 36px;
    min-height: 36px;
  }
`;

const NavigationButton = styled(PaginationButton)`
  background: var(--background-tertiary);
  border-color: var(--border-secondary);
  color: var(--text-secondary);
  
  &:hover:not(:disabled) {
    background: var(--accent-primary);
    color: #ffffff;
    border-color: var(--accent-primary);
  }
`;

const PaginationInfo = styled.div`
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  margin: 0 20px;
  padding: 8px 16px;
  background: var(--background-tertiary);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-primary);
  
  @media (max-width: 768px) {
    font-size: 12px;
    margin: 0 12px;
    padding: 6px 12px;
  }
`;

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange
}) => {
  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  if (totalPages <= 1) return null;

  const visiblePages = getVisiblePages();
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <PaginationContainer>
      <NavigationButton
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
        title="Previous page"
      >
        ←
      </NavigationButton>
      
      {visiblePages.map((page, index) => (
        <PaginationButton
          key={index}
          active={page === currentPage}
          disabled={page === '...'}
          onClick={() => typeof page === 'number' ? onPageChange(page) : undefined}
          title={typeof page === 'number' ? `Go to page ${page}` : undefined}
        >
          {page}
        </PaginationButton>
      ))}
      
      <NavigationButton
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
        title="Next page"
      >
        →
      </NavigationButton>
      
      <PaginationInfo>
        <strong>{startItem}-{endItem}</strong> of <strong>{totalItems}</strong> items
      </PaginationInfo>
    </PaginationContainer>
  );
};